/**
 * Hook for saving/updating entities (handleSave and handleApply logic)
 * 
 * This hook handles:
 * 1. Form validation
 * 2. Building entity data from form data
 * 3. PolicyRule-specific attribute normalization
 * 4. Create vs Update (PATCH) logic
 * 5. Error handling
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ScimEntityService } from '../services/ScimEntityService';
import { ScimUserService } from '../services/ScimUserService';
import { validateFormData } from '../utils/formValidationUtils';
import { getAttributeName } from '../utils/schemaAttributeUtils';
import { extractErrorMessage } from '../utils/apiUtils';
import {
    generatePatchOperationsWithArrays,
    compareObjects,
    normalizeForComparison,
    normalizeReference
} from '../utils/patchOperationUtils';
import { setFormDataToCurrentData } from '../utils/setSchemaUtils';
import { createEntityRef, getEntityValueFromARef, getId, normalizeKey } from '../types';
import { isARef } from '../utils/entityPropertyUtils';
import { extractReferenceId, extractReferenceDisplayName } from '../utils/referenceUtils';

export interface EntitySaveOptions {
    entityType: string;
    entityId?: string | null;
    isEditMode: boolean;
    isPolicyRule: boolean;
    isSet: boolean;
    isPerson: boolean;
    entityLabel: string;
    relativeTargetAttributeError: string | null;
}

export interface EntitySaveState {
    formData: Record<string, any>;
    originalData: Record<string, any> | null;
    schemaAttributes: any[];
    existingEntity?: any;
    /** Encrypted attribute names to send as null/empty on save (clear-on-save). */
    encryptedFieldsClearOnSave?: string[];
    /** Plain attribute names that are read-only (tier2/tier3 Binding.ReadOnly). Excluded from create payload. */
    readOnlyAttributeNames?: string[];
    /** Pending ids to remove per large array-ref attribute (e.g. MemberTransitive). Not stored in formData to avoid implying that list is the full value. */
    deferredArrayPendingRemoves?: Record<string, string[]>;
    /** Pending entities to add per large array-ref attribute. Not stored in formData for the same reason. */
    deferredArrayPendingAdds?: Record<string, Array<{ Id: string; DisplayName?: string; EntityType?: string }>>;
}

export interface EntitySaveCallbacks {
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    setFormData: (data: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)) => void;
    setOriginalData: (data: Record<string, any> | null) => void;
    onSuccess?: (result: any) => void;
    onClose: () => void;
    /** Called after Apply succeeds so the dialog can clear deferred array state (pending removes/adds). */
    onApplySuccess?: () => void;
}

export interface UseEntitySaveResult {
    handleSave: () => Promise<void>;
    handleApply: () => Promise<void>;
    buildEntityData: () => Record<string, any>;
    buildPatchOperations: (stateOverride?: Partial<Pick<EntitySaveState, 'formData' | 'originalData'>>) => any[];
}


/**
 * Strip keys from payload whose normalized name is in the read-only set (tier2/tier3 Binding.ReadOnly).
 * Uses normalizeKey so "IsSystem" and "isSystem" match; does not hard-code attribute names.
 */
function stripReadOnlyFromPayload(
    payload: Record<string, any>,
    readOnlyAttributeNames: string[]
): Record<string, any> {
    if (!readOnlyAttributeNames?.length) return payload;
    const readOnlySet = new Set(readOnlyAttributeNames.map((n) => normalizeKey(n)));
    const result = { ...payload };
    for (const key of Object.keys(result)) {
        if (readOnlySet.has(normalizeKey(key))) delete result[key];
    }
    return result;
}

/**
 * Build entity data from form data with PolicyRule-specific handling.
 * When readOnlyAttributeNames is provided (create), keys with Binding.ReadOnly are stripped from the payload (tier2/tier3; no hard-coded names).
 */
function buildEntityDataFromForm(
    formData: Record<string, any>,
    schemaAttributes: any[],
    isPolicyRule: boolean,
    isSet: boolean,
    readOnlyAttributeNames?: string[]
): Record<string, any> {
    // Set create payload uses camelCase for most keys (per API)
    if (isSet) {
        const typeStr = (getEntityValueFromARef('Type', formData) ?? 'Manual').toString().toLowerCase();
        const filterScopeVal = getEntityValueFromARef('FilterScope', formData);
        const filterScopeStr = filterScopeVal != null && typeof filterScopeVal === 'object'
            ? (extractReferenceId(filterScopeVal) ?? extractReferenceDisplayName(filterScopeVal) ?? '').toString().trim()
            : (filterScopeVal ?? '').toString().trim();
        const payload: Record<string, any> = {
            displayName: (getEntityValueFromARef('DisplayName', formData) ?? '').toString(),
            filterScope: filterScopeStr || undefined,
            type: typeStr === 'enumerated' ? 'Enumerated' : 'Manual',
            isSystem: getEntityValueFromARef('IsSystem', formData) ?? false
        };
        const description = getEntityValueFromARef('Description', formData);
        if (description != null && String(description).trim()) payload.description = description;
        const manualMembers = getEntityValueFromARef('ManualSetMember', formData);
        if (typeStr === 'manual' && Array.isArray(manualMembers) && manualMembers.length > 0) {
            payload.ManualSetMember = manualMembers.map((item: any) =>
                typeof item === 'string' ? item.trim() : getId(item)
            );
        }
        const filterVal = getEntityValueFromARef('Filter', formData);
        if (typeStr === 'enumerated' && filterVal != null && String(filterVal).trim()) {
            payload.filter = filterVal;
        }
        return stripReadOnlyFromPayload(payload, readOnlyAttributeNames ?? []);
    }

    const entityData: Record<string, any> = {};

    schemaAttributes.forEach((attr: any) => {
        const name = attr.name || attr.id;
        const namePlain = attributeNamePlain(name); // strip "$." so schema "$.RequestorSet" matches form key RequestorSet and API gets "RequestorSet"
        let value = getEntityValueFromARef(name, formData);

        // PolicyRule: values for set/reference fields are stored under canonical keys (RequestorSet, TargetSet, etc.); resolve so create payload includes them (name may be "$.RequestorSet" from schema)
        if (isPolicyRule && value === undefined) {
            const nameRef = createEntityRef(name);
            if (isARef(nameRef, 'RequestorSet')) value = getEntityValueFromARef('RequestorSet', formData);
            else if (isARef(nameRef, 'TargetSet')) value = getEntityValueFromARef('TargetSet', formData);
            else if (isARef(nameRef, 'TargetPostOpSet')) value = getEntityValueFromARef('TargetPostOpSet', formData);
        }

        // Special handling for Tags field - always convert to array
        if (isARef(createEntityRef(name), 'Tags')) {
            if (typeof value === 'string' && value.trim() !== '') {
                // Convert comma-separated string to array
                value = value.split(',').map((v: string) => v.trim()).filter((v: string) => v);
            } else if (!Array.isArray(value)) {
                // If it's not a string and not an array, make it an empty array or wrap it
                value = value ? [value] : [];
            }
            // Ensure it's an array (even if empty)
            if (!Array.isArray(value)) {
                value = [];
            }
        }

        // PossessiveActorsAttributes (PSGroup): form stores comma-delimited string; API expects array of attribute ids
        if (isARef(createEntityRef(name), 'PossessiveActorsAttributes')) {
            if (typeof value === 'string' && value.trim() !== '') {
                value = value.split(',').map((v: string) => v.trim()).filter((v: string) => v);
            } else if (!Array.isArray(value)) {
                value = value ? [value] : [];
            }
            if (!Array.isArray(value)) {
                value = [];
            }
        }

        // PolicyRule: Handle AllAttributes toggle; attribute values stay as-is (ids, no stripping or adding $.)
        if (isPolicyRule && isARef(createEntityRef(name), 'Attributes')) {
            const allAttributes = getEntityValueFromARef('AllAttributes', formData) !== false; // Default to true
            if (allAttributes) {
                value = null; // Set to null when AllAttributes is true
            } else {
                const attributes = getEntityValueFromARef(name, formData);
                if (Array.isArray(attributes) && attributes.length > 0) {
                    value = attributes.map((attr: any) => {
                        if (typeof attr === 'string') return attr;
                        if (attr != null && typeof attr === 'object') {
                            const id = (attr.Id ?? attr.id ?? attr.DisplayName ?? attr.displayName ?? '').toString().trim();
                            return id || null;
                        }
                        return null;
                    }).filter((id: any) => id != null && id !== '');
                } else {
                    value = [];
                }
            }
        }
        // RelativeTargetAttribute: pass as-is (no stripping or adding $.)

        // Multi-valued reference (e.g. Owner): API expects array of ids, not [{ Id: "..." }]
        if (attr.multiValued && attr.isReference && Array.isArray(value)) {
            value = value
                .filter((item: any) => item != null && item !== '')
                .map((item: any) => (typeof item === 'string' ? item.trim() : getId(item)));
        }

        // Single-valued reference: form may store EntityRef; API expects id string
        if (!attr.multiValued && attr.isReference === true && value != null && value !== '') {
            value = getId(value);
        }

        // PolicyRule: single-valued set refs (RequestorSet, TargetSet, TargetPostOpSet) — API expects id string, not object
        if (isPolicyRule && value != null && value !== '') {
            const nameRef = createEntityRef(name);
            if (isARef(nameRef, 'RequestorSet') || isARef(nameRef, 'TargetSet') || isARef(nameRef, 'TargetPostOpSet')) {
                value = getId(value);
            }
        }

        // Only include fields that have values (or are booleans). Output keys never use "$." — API expects DisplayName, RequestorSet, etc.
        if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value) && value.length === 0) {
                return; // Skip empty arrays
            }
            const nameRef = createEntityRef(name);
            const canonicalKey =
                isPolicyRule && isARef(nameRef, 'RequestorSet') ? 'RequestorSet'
                    : isPolicyRule && isARef(nameRef, 'TargetSet') ? 'TargetSet'
                        : isPolicyRule && isARef(nameRef, 'TargetPostOpSet') ? 'TargetPostOpSet'
                            : null;
            const outputKey = canonicalKey ?? namePlain ?? name;
            entityData[outputKey] = value;
        } else if (attr.type === 'boolean') {
            entityData[namePlain || name] = value || false;
        } else if (isPolicyRule && isARef(createEntityRef(name), 'Attributes') && getEntityValueFromARef('AllAttributes', formData) !== false) {
            entityData[namePlain || name] = null;
        }
    });

    return stripReadOnlyFromPayload(entityData, readOnlyAttributeNames ?? []);
}

/** API expects attribute paths without $. prefix (e.g. "DisplayName" not "$.DisplayName"). */
function pathForPatchOp(name: string): string {
    return (name ?? '').toString().trim().replace(/^\$\./, '') || (name ?? '');
}

/** Normalize schema attribute name for comparison (strip leading "$." so "$.RequestorSet" matches "RequestorSet"). */
function attributeNamePlain(name: string | undefined): string {
    return pathForPatchOp((name ?? '').toString().trim()) || (name ?? '');
}

/** Map: attribute plain name -> { multiValued }. Only attributes declared as references (tier3/schema isReference) are included. */
function buildReferenceAttributeMap(
    schemaAttributes: any[],
): Map<string, { multiValued: boolean }> {
    const map = new Map<string, { multiValued: boolean }>();
    schemaAttributes.forEach((attr: any) => {
        if (attr.isReference !== true) return;
        const plain = attributeNamePlain(attr.name || attr.id || attr.value);
        if (!plain) return;
        map.set(plain, { multiValued: !!attr.multiValued });
    });
    return map;
}

/** Resolve data key to plain name for ref-map lookup (form may use "RequestorSet" or "$.RequestorSet"). */
function plainKeyForDataKey(key: string): string {
    return attributeNamePlain(key);
}

/**
 * Normalize only reference-typed attributes to id(s). Uses tier3/schema isReference; non-reference values are left unchanged.
 */
function normalizeDataForPatch(
    data: Record<string, any>,
    referenceAttributeMap: Map<string, { multiValued: boolean }>
): Record<string, any> {
    if (referenceAttributeMap.size === 0) return { ...data };
    const out: Record<string, any> = {};
    for (const key of Object.keys(data)) {
        const plain = plainKeyForDataKey(key);
        const refMeta = referenceAttributeMap.get(plain);
        if (!refMeta) {
            out[key] = data[key];
            continue;
        }
        const value = data[key];
        if (value == null || value === '') {
            out[key] = value;
            continue;
        }
        if (refMeta.multiValued && Array.isArray(value)) {
            out[key] = value
                .filter((item: any) => item != null && item !== '')
                .map((item: any) => (typeof item === 'string' ? item.trim() : getId(item)));
        } else if (!refMeta.multiValued && typeof value === 'object' && !Array.isArray(value)) {
            out[key] = getId(value);
        } else {
            out[key] = value;
        }
    }
    return out;
}

/**
 * Normalize form/original snapshots the same way as before building PATCH ops
 * (reference-typed attributes → id(s) per tier3 schema). Use for diff/preview parity with Expected Payload.
 */
export function normalizeFormDataForPatchComparison(
    data: Record<string, any>,
    schemaAttributes: any[]
): Record<string, any> {
    const referenceAttributeMap = buildReferenceAttributeMap(schemaAttributes);
    return normalizeDataForPatch(data, referenceAttributeMap);
}

/** Same leaf normalization as JsonDiffViewer.compareObjects — detect real changes for dynamic/ref-shaped fields. */
function valuesDifferForStandardPatch(a: any, b: any): boolean {
    const na = normalizeReference(a);
    const nb = normalizeReference(b);
    if (na === nb) return false;
    if (na == null && nb == null) return false;
    // Form number inputs vs API string numbers (dynamic / tier2 fields)
    if (typeof na === 'number' && typeof nb === 'string' && nb.trim() !== '' && !Number.isNaN(Number(nb)) && na === Number(nb)) {
        return false;
    }
    if (typeof nb === 'number' && typeof na === 'string' && na.trim() !== '' && !Number.isNaN(Number(na)) && nb === Number(na)) {
        return false;
    }
    return JSON.stringify(na) !== JSON.stringify(nb);
}

/**
 * Build PATCH operations for standard entity types
 */
function buildStandardPatchOperations(
    formData: Record<string, any>,
    originalData: Record<string, any>,
    schemaAttributes: any[]
): any[] {
    const operations: any[] = [];

    schemaAttributes.forEach((attr: any) => {
        const name = getAttributeName(attr);
        if (!name) return;
        const path = pathForPatchOp(name);
        let newValue = getEntityValueFromARef(name, formData);
        let oldValue = getEntityValueFromARef(name, originalData);

        // PossessiveActorsAttributes: form stores comma-delimited string; API expects array
        if (isARef(createEntityRef(name), 'PossessiveActorsAttributes')) {
            if (typeof newValue === 'string' && newValue.trim() !== '') {
                newValue = newValue.split(',').map((v: string) => v.trim()).filter((v: string) => v);
            } else if (newValue != null && newValue !== '' && !Array.isArray(newValue)) {
                newValue = [newValue];
            } else if (newValue == null || newValue === '') {
                newValue = [];
            }
            if (typeof oldValue === 'string' && oldValue.trim() !== '') {
                oldValue = oldValue.split(',').map((v: string) => v.trim()).filter((v: string) => v);
            } else if (oldValue != null && oldValue !== '' && !Array.isArray(oldValue)) {
                oldValue = [oldValue];
            } else if (oldValue == null || oldValue === '') {
                oldValue = [];
            }
        }

        // Reference attributes are already normalized to id(s) by normalizeDataForPatch before this builder runs

        if (valuesDifferForStandardPatch(newValue, oldValue)) {
            const isArrayField = attr.multiValued || Array.isArray(oldValue) || Array.isArray(newValue);

            // Clearing the value (empty or null): use replace or delete, not remove with an empty array.
            // Replace is applicable to all attribute types; a delete (remove with no value) would also work to clear the attribute.
            if (newValue === undefined || newValue === null || newValue === '' ||
                (Array.isArray(newValue) && newValue.length === 0)) {
                if (isArrayField) {
                    // When removing all array values, use replace with [] (or delete). Do not use remove with value: [].
                    operations.push({ op: 'replace', path, value: [] });
                } else {
                    operations.push({ op: 'replace', path, value: null });
                }
            } else if (isArrayField && Array.isArray(oldValue) && Array.isArray(newValue)) {
                // For array fields, check if we're only adding values (not removing or reordering)
                const oldArray = oldValue || [];
                const newArray = newValue || [];

                // Check if all old values are still in the new array (no removals)
                const allOldValuesPresent = oldArray.every((oldItem: any) => {
                    return newArray.some((newItem: any) => {
                        // Deep equality check for objects, simple equality for primitives
                        if (typeof oldItem === 'object' && oldItem !== null && typeof newItem === 'object' && newItem !== null) {
                            return JSON.stringify(oldItem) === JSON.stringify(newItem);
                        }
                        return oldItem === newItem;
                    });
                });

                // If all old values are present and new array is longer, we're only adding
                if (allOldValuesPresent && newArray.length > oldArray.length) {
                    // Find the new values (values in newArray that aren't in oldArray)
                    const newValues = newArray.filter((newItem: any) => {
                        return !oldArray.some((oldItem: any) => {
                            if (typeof oldItem === 'object' && oldItem !== null && typeof newItem === 'object' && newItem !== null) {
                                return JSON.stringify(oldItem) === JSON.stringify(newItem);
                            }
                            return oldItem === newItem;
                        });
                    });

                    // Use add operation with just the new values
                    if (newValues.length > 0) {
                        operations.push({ op: 'add', path, value: newValues });
                    }
                } else {
                    // If values were removed or reordered, use replace with the full array
                    operations.push({ op: 'replace', path, value: newValue });
                }
            } else {
                // Non-array field or array field that wasn't an array before - use replace
                operations.push({ op: 'replace', path, value: newValue });
            }
        }
    });

    return operations;
}

/**
 * Resolve a form value by key or $. prefixed key (tier3 may use either).
 */
function getPolicyRuleFormValue(formData: Record<string, any>, plainKey: string): any {
    const dottedKey = plainKey.startsWith('$.') ? plainKey : `$.${plainKey}`;
    return formData[plainKey] ?? formData[dottedKey];
}

/**
 * Build PATCH operations for PolicyRule entities
 */
function buildPolicyRulePatchOperations(
    formData: Record<string, any>,
    originalData: Record<string, any>
): any[] {
    // Normalize formData for comparison (handle AllAttributes/Attributes; support both key variants)
    const normalizedFormData: Record<string, any> = { ...formData };
    const allAttributes = getPolicyRuleFormValue(formData, 'AllAttributes');
    const attributesRaw = getPolicyRuleFormValue(formData, 'Attributes');
    if (allAttributes !== false) {
        normalizedFormData.AllAttributes = true;
        normalizedFormData.Attributes = null;
    } else {
        normalizedFormData.AllAttributes = false;
        if (Array.isArray(attributesRaw)) {
            normalizedFormData.Attributes = attributesRaw.map((attr: any) => {
                if (typeof attr === 'string') return attr;
                if (attr != null && typeof attr === 'object') {
                    const id = (attr.Id ?? attr.id ?? attr.DisplayName ?? attr.displayName ?? '').toString().trim();
                    return id || null;
                }
                return null;
            }).filter((id: any) => id != null && id !== '');
        } else {
            normalizedFormData.Attributes = [];
        }
    }

    const originalNorm = normalizeForComparison(originalData);
    const currentNorm = normalizeForComparison(normalizedFormData);
    const differences = compareObjects(originalNorm, currentNorm, '', ['Id', 'EntityType']);
    const arrayFields = ['Attributes'];
    const excludedPaths = ['Id', 'EntityType'];

    return generatePatchOperationsWithArrays(originalData, normalizedFormData, differences, arrayFields, excludedPaths);
}

/**
 * Build PATCH operations for Set entities (ManualSetMember and Filter as array fields).
 */
function buildSetPatchOperations(
    formData: Record<string, any>,
    originalData: Record<string, any>,
    schemaAttributes: any[]
): any[] {
    const currentData = setFormDataToCurrentData(formData, originalData, schemaAttributes);
    const differences = compareObjects(originalData, currentData, '', ['Id', 'EntityType']);
    const arrayFields =
        (schemaAttributes ?? [])
            .filter((a: any) => a?.multiValued)
            .map((a: any) => pathForPatchOp(getAttributeName(a)))
            .filter((p: string) => p !== '');
    const excludedPaths = ['Id', 'EntityType'];
    return generatePatchOperationsWithArrays(originalData, currentData, differences, arrayFields, excludedPaths);
}

export function useEntitySave(
    options: EntitySaveOptions,
    state: EntitySaveState,
    callbacks: EntitySaveCallbacks,
    entityService: ScimEntityService,
    userService: ScimUserService
): UseEntitySaveResult {
    const queryClient = useQueryClient();
    const {
        entityType,
        entityId,
        isEditMode,
        isPolicyRule,
        isSet,
        isPerson,
        entityLabel,
        relativeTargetAttributeError
    } = options;

    const {
        formData,
        originalData,
        schemaAttributes,
        existingEntity,
        encryptedFieldsClearOnSave = [],
        readOnlyAttributeNames = [],
        deferredArrayPendingRemoves = {},
        deferredArrayPendingAdds = {}
    } = state;

    const {
        setLoading,
        setError,
        setFormData,
        setOriginalData,
        onSuccess,
        onClose,
        onApplySuccess
    } = callbacks;

    const buildEntityData = useCallback(() => {
        return buildEntityDataFromForm(formData, schemaAttributes, isPolicyRule, isSet, readOnlyAttributeNames);
    }, [formData, schemaAttributes, isPolicyRule, isSet, readOnlyAttributeNames]);

    const buildPatchOperations = useCallback((stateOverride?: Partial<Pick<EntitySaveState, 'formData' | 'originalData'>>) => {
        const effectiveFormData = stateOverride?.formData ?? formData;
        const effectiveOriginalData = stateOverride?.originalData ?? originalData;
        if (!effectiveOriginalData) return [];

        // Normalize ref-shaped values to id(s) only for attributes declared as references (tier3/schema isReference), before patch builders
        const referenceAttributeMap = buildReferenceAttributeMap(schemaAttributes);
        const normalizedFormData = normalizeDataForPatch(effectiveFormData, referenceAttributeMap);
        const normalizedOriginalData = normalizeDataForPatch(effectiveOriginalData, referenceAttributeMap);

        let operations: any[];
        if (isSet) {
            operations = buildSetPatchOperations(normalizedFormData, normalizedOriginalData, schemaAttributes);
        } else if (isPolicyRule) {
            operations = buildPolicyRulePatchOperations(normalizedFormData, normalizedOriginalData);
        } else {
            operations = buildStandardPatchOperations(normalizedFormData, normalizedOriginalData, schemaAttributes);
        }
        // Add replace null for encrypted fields that user chose to clear on save
        encryptedFieldsClearOnSave.forEach((name) => {
            const path = pathForPatchOp(name);
            const existing = operations.find((op: any) => op.path === path);
            if (existing) {
                existing.op = 'replace';
                existing.value = null;
            } else {
                operations.push({ op: 'replace', path, value: null });
            }
        });
        // Deferred large array-ref: pending removes (one remove op per id per attribute)
        Object.entries(deferredArrayPendingRemoves).forEach(([field, ids]) => {
            if (!ids?.length) return;
            const path = pathForPatchOp(field);
            ids.forEach((id) => {
                operations.push({ op: 'remove', path, value: id });
            });
        });
        // Deferred large array-ref: pending adds (one add op per attribute with array of ids)
        Object.entries(deferredArrayPendingAdds).forEach(([field, items]) => {
            if (!items?.length) return;
            const path = pathForPatchOp(field);
            const ids = items.map((item) => item.Id ?? '').filter((id) => id !== '');
            if (ids.length > 0) {
                operations.push({ op: 'add', path, value: ids });
            }
        });
        return operations;
    }, [formData, originalData, schemaAttributes, isPolicyRule, isSet, encryptedFieldsClearOnSave, deferredArrayPendingRemoves, deferredArrayPendingAdds]);

    const handleSave = useCallback(async () => {
        // Validate form data
        const validationResult = validateFormData(formData, schemaAttributes, {
            isPolicyRule,
            relativeTargetAttributeError
        });

        if (!validationResult.isValid) {
            setError(validationResult.error);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const entityData = buildEntityDataFromForm(formData, schemaAttributes, isPolicyRule, isSet, readOnlyAttributeNames);
            let result;

            if (isEditMode && entityId && originalData) {
                // Update existing entity using PATCH (includes encryptedFieldsClearOnSave)
                const operations = buildPatchOperations();

                if (operations.length > 0) {
                    const patchRequest = {
                        schemas: ['urn:ietf:params:scim:api:messages:2.0:PatchOp'],
                        Operations: operations
                    };

                    if (isPerson) {
                        result = await userService.patchUser(entityId, patchRequest, entityType);
                    } else {
                        result = await entityService.patchEntity(entityId, patchRequest, entityType);
                    }
                } else {
                    result = existingEntity;
                }
            } else {
                // Create new entity
                result = await entityService.createEntity(entityType, entityData);
            }

            queryClient.invalidateQueries({ queryKey: ['entities', entityType] });
            if (entityId) {
                queryClient.invalidateQueries({ queryKey: ['entity', entityType, entityId] });
            }
            if (entityType.toLowerCase() === 'set') {
                queryClient.invalidateQueries({ queryKey: ['sets'] });
            }

            if (onSuccess) {
                onSuccess(result);
            }

            // Reset form
            setFormData({});
            setOriginalData(null);
            onClose();
        } catch (err: any) {
            console.error(`Error saving ${entityLabel}:`, err);
            const { extractErrorMessage } = await import('../utils/apiUtils');
            const errorMessage = extractErrorMessage(err);
            setError(errorMessage || `Failed to save ${entityLabel}`);
        } finally {
            setLoading(false);
        }
    }, [
        formData, schemaAttributes, isPolicyRule, isSet, relativeTargetAttributeError,
        isEditMode, entityId, originalData, isPerson, entityType, entityLabel,
        existingEntity, entityService, userService, onSuccess, onClose,
        setLoading, setError, setFormData, setOriginalData, buildPatchOperations
    ]);

    const handleApply = useCallback(async () => {
        // Validate form data
        const validationResult = validateFormData(formData, schemaAttributes, {
            isPolicyRule,
            relativeTargetAttributeError
        });

        if (!validationResult.isValid) {
            setError(validationResult.error);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const entityData = buildEntityDataFromForm(formData, schemaAttributes, isPolicyRule, isSet, readOnlyAttributeNames);
            let result;

            if (isEditMode && entityId && originalData) {
                // Update existing entity using PATCH (includes encryptedFieldsClearOnSave)
                const operations = buildPatchOperations();

                if (operations.length > 0) {
                    const patchRequest = {
                        schemas: ['urn:ietf:params:scim:api:messages:2.0:PatchOp'],
                        Operations: operations
                    };

                    if (isPerson) {
                        result = await userService.patchUser(entityId, patchRequest, entityType);
                    } else {
                        result = await entityService.patchEntity(entityId, patchRequest, entityType);
                    }
                } else {
                    result = existingEntity;
                }
            } else {
                // Create new entity
                result = await entityService.createEntity(entityType, entityData);
            }

            queryClient.invalidateQueries({ queryKey: ['entities', entityType] });
            if (entityId) {
                queryClient.invalidateQueries({ queryKey: ['entity', entityType, entityId] });
            }
            if (entityType.toLowerCase() === 'set') {
                queryClient.invalidateQueries({ queryKey: ['sets'] });
            }

            // When the server gives OK on the patch, set the altered entity (form state) as the new baseline.
            // Use the entity as we have it in the form — not the patch request (Operations) and not the response body.
            if (result !== undefined) {
                const alteredEntity: Record<string, any> = { ...formData };
                setOriginalData(alteredEntity);
                onApplySuccess?.();
            }

            // NOTE: We intentionally do NOT call onSuccess here.
            // onSuccess is meant to signal "save complete, close dialog" which Apply doesn't do.
            // The parent component's onSuccess typically closes the dialog, which we don't want for Apply.
        } catch (err: any) {
            console.error(`Error applying changes to ${entityLabel}:`, err);
            const errorMessage = extractErrorMessage(err);
            setError(errorMessage || `Failed to apply changes to ${entityLabel}`);
        } finally {
            setLoading(false);
        }
    }, [
        formData, schemaAttributes, isPolicyRule, isSet, relativeTargetAttributeError,
        isEditMode, entityId, originalData, isPerson, entityType, entityLabel,
        existingEntity, entityService, userService,
        setLoading, setError, setOriginalData, buildPatchOperations, onApplySuccess
    ]);

    return {
        handleSave,
        handleApply,
        buildEntityData,
        buildPatchOperations
    };
}
